import { useState, useEffect, useCallback, useRef } from "react";
import { Badge } from "@/shared/components/ui/badge";
import { Wifi, WifiOff, WifiLow, AlertTriangle } from "lucide-react";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@/shared/components/ui/alert";

export type ConnectionStatus = "good" | "moderate" | "poor" | "offline";

export interface ConnectionQuality {
  status: ConnectionStatus;
  latency: number | null;
  isOnline: boolean;
  isModerate: boolean;
  isPoor: boolean;
  isOffline: boolean;
  lastChecked: Date | null;
  checkNow: () => Promise<void>;
}

export interface UseConnectionQualityOptions {
  /** Ping endpoint URL. Default: '/api/ping' */
  pingEndpoint?: string;
  /** Polling interval in ms. Default: 15000 (15s) */
  pollingInterval?: number;
  /** Polling interval when degraded in ms. Default: 5000 (5s) */
  degradedPollingInterval?: number;
  /** Latency threshold for moderate status in ms. Default: 200 */
  moderateThreshold?: number;
  /** Latency threshold for poor status in ms. Default: 500 */
  poorThreshold?: number;
  /** Request timeout in ms. Default: 10000 (10s) */
  timeout?: number;
  /** Number of samples for rolling average. Default: 3 */
  sampleSize?: number;
}

const DEFAULT_OPTIONS: Required<UseConnectionQualityOptions> = {
  pingEndpoint: "/api/ping",
  pollingInterval: 15000,
  degradedPollingInterval: 5000,
  moderateThreshold: 200,
  poorThreshold: 500,
  timeout: 10000,
  sampleSize: 3,
};

export function useConnectionQuality(
  options?: UseConnectionQualityOptions,
): ConnectionQuality {
  const config = { ...DEFAULT_OPTIONS, ...options };

  const [isOnline, setIsOnline] = useState<boolean>(navigator.onLine);
  const [latency, setLatency] = useState<number | null>(null);
  const [status, setStatus] = useState<ConnectionStatus>(
    navigator.onLine ? "good" : "offline",
  );
  const [lastChecked, setLastChecked] = useState<Date | null>(null);

  const latencySamples = useRef<number[]>([]);
  const consecutiveFailures = useRef<number>(0);

  const calculateStatus = useCallback(
    (avgLatency: number | null, online: boolean): ConnectionStatus => {
      if (!online) return "offline";
      if (avgLatency === null) return "good"; // No data yet, assume good
      if (consecutiveFailures.current >= 2) return "poor";
      if (avgLatency >= config.poorThreshold) return "poor";
      if (avgLatency >= config.moderateThreshold) return "moderate";
      return "good";
    },
    [config.moderateThreshold, config.poorThreshold],
  );

  const ping = useCallback(async (): Promise<void> => {
    if (!navigator.onLine) {
      setStatus("offline");
      setLatency(null);
      setLastChecked(new Date());
      return;
    }

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), config.timeout);

    const startTime = performance.now();

    try {
      const response = await fetch(config.pingEndpoint, {
        method: "GET",
        signal: controller.signal,
        cache: "no-store",
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error(`Ping failed with status ${response.status}`);
      }

      const endTime = performance.now();
      const measuredLatency = Math.round(endTime - startTime);

      // Update rolling average
      latencySamples.current.push(measuredLatency);
      if (latencySamples.current.length > config.sampleSize) {
        latencySamples.current.shift();
      }

      const avgLatency = Math.round(
        latencySamples.current.reduce((a, b) => a + b, 0) /
          latencySamples.current.length,
      );

      consecutiveFailures.current = 0;
      setLatency(avgLatency);
      setStatus(calculateStatus(avgLatency, true));
      setLastChecked(new Date());
    } catch {
      clearTimeout(timeoutId);
      consecutiveFailures.current += 1;

      // Keep previous latency but update status based on failures
      setStatus(calculateStatus(latency, navigator.onLine));
      setLastChecked(new Date());
    }
  }, [
    config.pingEndpoint,
    config.timeout,
    config.sampleSize,
    calculateStatus,
    latency,
  ]);

  // Handle online/offline events
  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      consecutiveFailures.current = 0;
      latencySamples.current = [];
      ping(); // Immediately check when coming back online
    };

    const handleOffline = () => {
      setIsOnline(false);
      setStatus("offline");
      setLatency(null);
    };

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, [ping]);

  // Polling with adaptive interval
  useEffect(() => {
    // Initial ping
    ping();

    const getInterval = () => {
      return status === "good"
        ? config.pollingInterval
        : config.degradedPollingInterval;
    };

    let intervalId: ReturnType<typeof setInterval>;

    const setupInterval = () => {
      intervalId = setInterval(() => {
        ping();
        // Re-setup interval if status changed
        clearInterval(intervalId);
        setupInterval();
      }, getInterval());
    };

    setupInterval();

    return () => clearInterval(intervalId);
  }, [ping, status, config.pollingInterval, config.degradedPollingInterval]);

  return {
    status,
    latency,
    isOnline,
    isModerate: status === "moderate",
    isPoor: status === "poor",
    isOffline: status === "offline",
    lastChecked,
    checkNow: ping,
  };
}

const statusConfig: Record<
  Exclude<ConnectionStatus, "good">,
  {
    label: string;
    variant: "secondary" | "destructive" | "outline";
    icon: typeof Wifi;
  }
> = {
  moderate: {
    label: "Mäßige Verbindung",
    variant: "secondary",
    icon: WifiLow,
  },
  poor: {
    label: "Schlechte Verbindung",
    variant: "destructive",
    icon: WifiLow,
  },
  offline: {
    label: "Offline",
    variant: "destructive",
    icon: WifiOff,
  },
};

export function ConnectionBadge({
  options,
}: {
  options?: UseConnectionQualityOptions;
}) {
  const { status } = useConnectionQuality(options);

  if (status === "good") {
    return null;
  }

  const config = statusConfig[status];

  return (
    <Badge variant={config.variant} className={`cursor-help gap-1.5`}>
      <config.icon className="h-3.5 w-3.5" />
      <span>{config.label}</span>
    </Badge>
  );
}

const alertStatus: Record<
  Exclude<ConnectionStatus, "good">,
  {
    title: string;
    variant: "destructive" | "default";
    className: string;
  }
> = {
  moderate: {
    title: "Mäßige Verbindung. Große Uploads können fehlschlagen.",
    variant: "default",
    className: "",
  },
  poor: {
    title: "Schlechte Verbindung. Funktionalität eingeschränkt.",
    variant: "destructive",
    className:
      "bg-destructive text-destructive-foreground *:data-[slot=alert-description]:text-destructive-foreground/80",
  },
  offline: {
    title: "Offline",
    variant: "destructive",
    className:
      "bg-destructive text-destructive-foreground *:data-[slot=alert-description]:text-destructive-foreground/80",
  },
};

export function ConnectionAlert({
  options,
  showLatency = true,
}: {
  options?: UseConnectionQualityOptions;
  showLatency?: boolean;
}) {
  const { status, latency } = useConnectionQuality(options);

  if (status === "good") {
    return null;
  }

  const config = alertStatus[status];

  return (
    <Alert variant={config.variant} className={config.className}>
      <AlertTriangle />
      <AlertTitle>{config.title}</AlertTitle>
      {showLatency && (
        <AlertDescription>
          Latenz: {latency !== null ? `${latency}ms` : "unbekannt"}.
        </AlertDescription>
      )}
    </Alert>
  );
}
